import { useState } from "react";
import { Info, AlertCircle, AlertTriangle, CheckCircle2, Radio, RadioTower } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useBroadcast, useBroadcastAdmin } from "@/hooks/useBroadcast";
import type { BannerVariant } from "@/services/broadcastApi";
import { cn } from "@/lib/utils";

const VARIANTS: { value: BannerVariant; label: string; icon: React.ElementType; colors: string }[] = [
  { value: "info",    label: "Info",    icon: Info,          colors: "border-blue-300 bg-blue-50 text-blue-800 data-[selected=true]:ring-2 data-[selected=true]:ring-blue-400" },
  { value: "error",   label: "Error",   icon: AlertCircle,   colors: "border-red-300 bg-red-50 text-red-800 data-[selected=true]:ring-2 data-[selected=true]:ring-red-400" },
  { value: "warning", label: "Warning", icon: AlertTriangle, colors: "border-yellow-300 bg-yellow-50 text-yellow-800 data-[selected=true]:ring-2 data-[selected=true]:ring-yellow-400" },
  { value: "success", label: "Success", icon: CheckCircle2,  colors: "border-teal-300 bg-teal-50 text-teal-800 data-[selected=true]:ring-2 data-[selected=true]:ring-teal-400" },
];

const BroadcastAdmin = () => {
  const { data: current, isLoading } = useBroadcast();
  const { set, hide } = useBroadcastAdmin();

  const [variant, setVariant] = useState<BannerVariant>("info");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [inline, setInline] = useState(false);

  const isActive = !!current?.is_active;

  const handlePublish = () => {
    if (!title.trim()) return;
    set.mutate({ variant, title: title.trim(), body: body.trim() || undefined, inline });
  };

  const handleDeactivate = () => hide.mutate();

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-8 max-w-xl">

      {/* Current status */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Current Status</h3>
        {isActive && current ? (
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                <RadioTower className="w-3 h-3" />
                Live
              </span>
              <span className="text-xs text-muted-foreground capitalize">{current.variant}</span>
            </div>
            <div>
              <p className="text-sm font-semibold">{current.title}</p>
              {current.body && <p className="text-sm text-muted-foreground mt-0.5">{current.body}</p>}
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeactivate}
              disabled={hide.isPending}
            >
              Deactivate Banner
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <Radio className="w-4 h-4" />
            No active broadcast banner.
          </div>
        )}
      </div>

      <Separator />

      {/* Compose form */}
      <div className="space-y-5">
        <h3 className="text-sm font-semibold">{isActive ? "Replace Banner" : "Publish New Banner"}</h3>

        {/* Variant picker */}
        <div className="space-y-2">
          <Label>Type</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {VARIANTS.map(({ value, label, icon: Icon, colors }) => (
              <button
                key={value}
                type="button"
                data-selected={variant === value}
                onClick={() => setVariant(value)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                  colors
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="space-y-1.5">
          <Label htmlFor="bc-title">Title <span className="text-destructive">*</span></Label>
          <Input
            id="bc-title"
            placeholder="e.g. Scheduled maintenance tonight"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Body */}
        <div className="space-y-1.5">
          <Label htmlFor="bc-body">Body text <span className="text-muted-foreground text-xs">(optional)</span></Label>
          <Textarea
            id="bc-body"
            placeholder="Additional details shown below the title"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        {/* Inline toggle */}
        <div className="flex items-center gap-3">
          <Switch id="bc-inline" checked={inline} onCheckedChange={setInline} />
          <Label htmlFor="bc-inline" className="cursor-pointer">
            Inline — show title and body on the same line
          </Label>
        </div>

        {/* Preview */}
        {title.trim() && (() => {
          const { icon: Icon, colors } = VARIANTS.find((v) => v.value === variant)!;
          return (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">Preview</Label>
              <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3 text-sm", colors)}>
                <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  {inline ? (
                    <span>
                      <span className="font-semibold">{title}</span>
                      {body && <span className="ml-1">{body}</span>}
                    </span>
                  ) : (
                    <>
                      <p className="font-semibold">{title}</p>
                      {body && <p className="mt-0.5">{body}</p>}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        <Button
          onClick={handlePublish}
          disabled={!title.trim() || set.isPending}
        >
          {isActive ? "Update & Republish" : "Publish Banner"}
        </Button>
      </div>
    </div>
  );
};

export default BroadcastAdmin;
